import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  CalendarDays, ChevronLeft, ChevronRight, Plus, Check, Users,
} from "lucide-react";
import { toast } from "sonner";
import PixelAvatar from "@/shared/components/PixelAvatar";
import { agentMood } from "@/shared/lib/agent-mood";
import { Popover, PopoverTrigger, PopoverContent } from "@/shared/components/ui/popover";
import {
  TaskDialog, ScheduleBadge, fmtTime, initialSchedule, chipLabel, buildCron, whenSaved,
} from "./TasksPage";
import { Breadcrumb } from "@/shared/components/Breadcrumb";
import { PageHeader, IconButton, BrandButton } from "@/shared/components/ui-kit";
import { useViewport } from "@/shared/hooks/useViewport";
import type { TaskDraft } from "./TasksPage";
import type { Agent, AgentTask, ScheduleSpec } from "@/shared/types";

/* ------------------------------- date helpers ------------------------------ */

const pad = (n: number) => String(n).padStart(2, "0");
const isoDate = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const sameDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
const addDays = (d: Date, n: number) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const addMonths = (d: Date, n: number) => { const x = new Date(d); x.setDate(1); x.setMonth(x.getMonth() + n); return x; };
const startOfWeek = (d: Date) => { const x = new Date(d); x.setHours(0, 0, 0, 0); x.setDate(x.getDate() - x.getDay()); return x; };
const startOfDay = (d: Date) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };

const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

/* --------------------------- occurrence expansion -------------------------- */

/* A task the grids can place. `occursOn` only matches specs it can read, so the
   tasks it selects all carry one — `tasksOn` is the single place that invariant
   is established, and the only place it is asserted. */
type ScheduledTask = AgentTask & { spec: ScheduleSpec };

interface Occurrence {
  agent: Agent;
  task: ScheduledTask;
}

/** What a dragged chip carries to the drop target. */
interface DragPayload {
  agentId: string;
  taskId: string;
  sourceDow: number;
}

type DragRef = React.RefObject<DragPayload | null>;

// Does this task's schedule spec fire on this calendar date?
function occursOn(spec: ScheduleSpec | undefined, date: Date): boolean {
  if (!spec) return false; // Ceiling: custom-cron / legacy tasks stay off the grid
  if (spec.mode === "once") return spec.date === isoDate(date);
  if (spec.freq === "daily") return true;
  if (spec.freq === "weekly") return (spec.days.length ? spec.days : [1]).includes(date.getDay());
  if (spec.freq === "monthly") return date.getDate() === Number(spec.dayOfMonth);
  return false;
}

// Flat [{agent, task}] across all agents, filtered + sorted by time, for one date.
function tasksOn(agents: Agent[], date: Date, hidden: Set<string>): Occurrence[] {
  const out: Occurrence[] = [];
  for (const a of agents) {
    if (hidden.has(a.id)) continue;
    for (const t of a.tasks || []) if (occursOn(t.spec, date)) out.push({ agent: a, task: t as ScheduledTask });
  }
  return out.sort((x, y) => (x.task.spec.time || "").localeCompare(y.task.spec.time || ""));
}

// New spec after dragging an occurrence (from sourceDow) onto targetDate at newTime.
function movedSpec(spec: ScheduleSpec, sourceDow: number, targetDate: Date, newTime?: string | null): ScheduleSpec {
  const s = { ...spec, time: newTime ?? spec.time };
  if (s.mode === "once") s.date = isoDate(targetDate);
  else if (s.freq === "weekly") {
    const days = (s.days.length ? s.days : [1]).map((d) => (d === sourceDow ? targetDate.getDay() : d));
    s.days = [...new Set(days)].sort((a, b) => a - b);
  } else if (s.freq === "monthly") s.dayOfMonth = targetDate.getDate();
  // daily: only the time moves — it still runs every day
  return s;
}

const tint = (hue: number, a: number) => `hsl(${hue} 60% 50% / ${a})`;

/* -------------------------------- task chip -------------------------------- */

function TaskChip({ agent, task, compact, shared, onDragStart, onDragEnd, onClick }: {
  agent: Agent;
  task: ScheduledTask;
  compact?: boolean;
  /** Sharing its hour with another chip: a narrow lane keeps only the time; the tint says whose it is. */
  shared?: boolean;
  onDragStart: () => void;
  onDragEnd: () => void;
  onClick: () => void;
}) {
  return (
    <button
      draggable
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      title={`${task.name}${task.paused ? " (paused)" : ""} — ${agent.name} · ${task.schedule}`}
      className="press w-full flex items-center gap-1.5 rounded-[6px] px-1.5 text-left cursor-grab active:cursor-grabbing overflow-hidden"
      style={{
        background: tint(agent.hue, 0.13),
        boxShadow: `inset 2.5px 0 0 hsl(${agent.hue} 55% 50%)`,
        height: compact ? 20 : WEEK_CHIP_H,
        paddingTop: compact ? 0 : 3,
        paddingBottom: compact ? 0 : 3,
        opacity: task.paused ? 0.45 : 1,
      }}
    >
      {compact ? (
        <>
          <span className="text-[11px] tabular-nums text-[var(--text-tertiary)] shrink-0">
            {fmtTime(task.spec.time)}
          </span>
          <span className="text-[11.5px] font-medium text-[var(--text-primary)] truncate">{task.name}</span>
        </>
      ) : (
        <div className="min-w-0">
          {/* A shared lane is too narrow for the name below 2xl: the time, the tint and the title say it */}
          <div className={`${shared ? "hidden 2xl:block" : ""} text-[11.5px] font-medium leading-[1.3] text-[var(--text-primary)] truncate`}>
            {task.name}
          </div>
          <div className="flex items-center gap-1 mt-[1px]">
            {!shared && <PixelAvatar seed={agent.seed} hue={agent.hue} emotion={agentMood(agent)} size={10} rounded={3} />}
            {/* A week column only fits the agent's name from 2xl; below it the avatar and tint say who */}
            <span className="text-[10.5px] text-[var(--text-tertiary)] truncate">
              {fmtTime(task.spec.time)}{!shared && <span className="hidden 2xl:inline"> · {agent.name}</span>}
            </span>
          </div>
        </div>
      )}
    </button>
  );
}

/* --------------------------------- week view ------------------------------- */

const HOUR_H = 48;
/* A week-grid chip is one height whatever the task's length, so it covers this many minutes of the grid.
   Occurrences closer together than that share the row side by side instead of being drawn over each other. */
const WEEK_CHIP_H = 40;
const CHIP_MINUTES = (WEEK_CHIP_H / HOUR_H) * 60;

/** Lane and lane count per occurrence: greedy columns within each run of overlapping chips. `items` sorted by time. */
function weekLanes(items: Occurrence[]): { lane: number; lanes: number }[] {
  const placed: { lane: number; lanes: number }[] = [];
  let group: number[] = [];
  let laneEnds: number[] = [];
  let groupEnd = -Infinity;
  const close = () => {
    group.forEach((i) => (placed[i].lanes = laneEnds.length));
    group = [];
    laneEnds = [];
    groupEnd = -Infinity;
  };
  items.forEach((o, i) => {
    const [h, m] = (o.task.spec.time || "09:00").split(":").map(Number);
    const startMin = h * 60 + m;
    if (startMin >= groupEnd) close();
    let lane = laneEnds.findIndex((end) => end <= startMin);
    if (lane === -1) lane = laneEnds.push(0) - 1;
    laneEnds[lane] = startMin + CHIP_MINUTES;
    groupEnd = Math.max(groupEnd, startMin + CHIP_MINUTES);
    placed[i] = { lane, lanes: 1 };
    group.push(i);
  });
  close();
  return placed;
}
const snapMinutes = (y: number) =>
  Math.max(0, Math.min(23.5 * 60, Math.round((y / HOUR_H) * 60 / 30) * 30));

function WeekView({ days, agents, hidden, dragRef, onMove, onClickTask, onClickSlot }: {
  days: Date[];
  agents: Agent[];
  hidden: Set<string>;
  dragRef: DragRef;
  onMove: (targetDate: Date, newTime: string | null) => void;
  onClickTask: (agent: Agent, task: AgentTask) => void;
  onClickSlot: (d: Date, time: string) => void;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [hoverDrop, setHoverDrop] = useState<{ di: number; minutes: number } | null>(null);
  const now = new Date();

  useEffect(() => {
    scrollRef.current && (scrollRef.current.scrollTop = 7 * HOUR_H);
  }, []);

  const minutesFromEvent = (e: React.DragEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    return snapMinutes(e.clientY - rect.top);
  };

  return (
    <div className="flex-1 min-h-0 flex flex-col fade-in">
      {/* Day header row */}
      <div className="shrink-0 flex border-b hairline pr-[10px]">
        <div className="w-[52px] shrink-0" />
        {days.map((d) => {
          const today = sameDay(d, now);
          return (
            <div key={+d} className="flex-1 min-w-0 py-2 text-center border-l hairline">
              <div className="section-label">
                {DOW[d.getDay()]}
              </div>
              <div
                className={`mx-auto mt-0.5 w-6 h-6 flex items-center justify-center rounded-full text-[13px] tabular-nums font-medium ${
                  today ? "bg-[var(--brand)] text-[var(--brand-foreground)]" : "text-[var(--text-primary)]"
                }`}
              >
                {d.getDate()}
              </div>
            </div>
          );
        })}
      </div>

      {/* Time grid */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto">
        <div className="flex relative" style={{ height: 24 * HOUR_H }}>
          {/* Hour gutter */}
          <div className="w-[52px] shrink-0 relative">
            {Array.from({ length: 23 }, (_, i) => i + 1).map((h) => (
              <div
                key={h}
                className="absolute right-2 text-[10.5px] tabular-nums text-[var(--text-quaternary)] -translate-y-1/2"
                style={{ top: h * HOUR_H }}
              >
                {fmtTime(`${pad(h)}:00`)}
              </div>
            ))}
          </div>

          {days.map((d, di) => {
            const items = tasksOn(agents, d, hidden);
            const placement = weekLanes(items);
            const today = sameDay(d, now);
            return (
              <div
                key={+d}
                className="flex-1 min-w-0 relative border-l hairline"
                onDragOver={(e) => {
                  e.preventDefault();
                  const minutes = minutesFromEvent(e);
                  setHoverDrop((p) => (p?.di === di && p?.minutes === minutes ? p : { di, minutes }));
                }}
                onDragLeave={() => setHoverDrop((p) => (p?.di === di ? null : p))}
                onDrop={(e) => {
                  e.preventDefault();
                  setHoverDrop(null);
                  const minutes = minutesFromEvent(e);
                  onMove(d, `${pad(Math.floor(minutes / 60))}:${pad(minutes % 60)}`);
                }}
                onClick={(e) => {
                  const rect = e.currentTarget.getBoundingClientRect();
                  const minutes = snapMinutes(e.clientY - rect.top);
                  onClickSlot(d, `${pad(Math.floor(minutes / 60))}:${pad(minutes % 60)}`);
                }}
              >
                {/* hour lines */}
                {Array.from({ length: 23 }, (_, i) => i + 1).map((h) => (
                  <div key={h} className="absolute inset-x-0 border-t hairline" style={{ top: h * HOUR_H }} />
                ))}

                {/* drop preview */}
                {hoverDrop?.di === di && dragRef.current && (
                  <div
                    className="absolute inset-x-1 h-[38px] rounded-[6px] ring-1 ring-[var(--hairline-focus)] bg-[var(--accent-active)] pointer-events-none z-10 flex items-center px-1.5 text-[11px] tabular-nums text-[var(--text-tertiary)]"
                    style={{ top: (hoverDrop.minutes / 60) * HOUR_H }}
                  >
                    {fmtTime(`${pad(Math.floor(hoverDrop.minutes / 60))}:${pad(hoverDrop.minutes % 60)}`)}
                  </div>
                )}

                {/* now line */}
                {today && (
                  <div
                    className="absolute inset-x-0 z-10 pointer-events-none"
                    style={{ top: ((now.getHours() * 60 + now.getMinutes()) / 60) * HOUR_H }}
                  >
                    <div className="h-[2px] bg-[var(--danger)] opacity-80" />
                    <div className="w-2 h-2 rounded-full bg-[var(--danger)] -mt-[5px] -ml-[4px]" />
                  </div>
                )}

                {/* occurrences — `items` is sorted by time, which weekLanes relies on */}
                {items.map(({ agent, task }, i) => {
                  const [h, m] = (task.spec.time || "09:00").split(":").map(Number);
                  const { lane, lanes } = placement[i];
                  return (
                    <div
                      key={`${agent.id}-${task.id}`}
                      className="absolute z-[5] px-0.5"
                      style={{ top: ((h * 60 + m) / 60) * HOUR_H, left: `${(lane / lanes) * 100}%`, width: `${100 / lanes}%` }}
                    >
                      <TaskChip
                        agent={agent}
                        task={task}
                        shared={lanes > 1}
                        onDragStart={() => { dragRef.current = { agentId: agent.id, taskId: task.id, sourceDow: d.getDay() }; }}
                        onDragEnd={() => { dragRef.current = null; setHoverDrop(null); }}
                        onClick={() => onClickTask(agent, task)}
                      />
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/* -------------------------------- month view ------------------------------- */

function MonthView({ cursor, agents, hidden, dragRef, onMove, onClickTask, onClickDay, onOverflowDay }: {
  cursor: Date;
  agents: Agent[];
  hidden: Set<string>;
  dragRef: DragRef;
  onMove: (targetDate: Date, newTime: string | null) => void;
  onClickTask: (agent: Agent, task: AgentTask) => void;
  onClickDay: (d: Date) => void;
  onOverflowDay: (d: Date) => void;
}) {
  const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
  const start = startOfWeek(first);
  const cells = Array.from({ length: 42 }, (_, i) => addDays(start, i));
  const now = new Date();
  const [hoverDi, setHoverDi] = useState<number | null>(null);

  return (
    <div className="flex-1 min-h-0 flex flex-col fade-in">
      <div className="shrink-0 grid grid-cols-7 border-b hairline">
        {DOW.map((d) => (
          <div key={d} className="py-1.5 text-center section-label">
            {d}
          </div>
        ))}
      </div>
      <div className="flex-1 min-h-0 grid grid-cols-7 grid-rows-6">
        {cells.map((d, i) => {
          const inMonth = d.getMonth() === cursor.getMonth();
          const today = sameDay(d, now);
          const items = tasksOn(agents, d, hidden);
          const MAX = 2; // Ceiling: fixed cap; measure row height if cells ever grow
          return (
            <div
              key={i}
              onClick={() => onClickDay(d)}
              onDragOver={(e) => { e.preventDefault(); setHoverDi(i); }}
              onDragLeave={() => setHoverDi((p) => (p === i ? null : p))}
              onDrop={(e) => { e.preventDefault(); setHoverDi(null); onMove(d, null); }}
              className={`min-h-0 border-b border-l hairline p-1 overflow-hidden cursor-pointer ${
                hoverDi === i ? "bg-[var(--accent-active)]" : inMonth ? "" : "bg-[var(--surface-sunk)]"
              }`}
            >
              <div
                className={`w-5 h-5 mb-0.5 flex items-center justify-center rounded-full text-[11.5px] tabular-nums ${
                  today
                    ? "bg-[var(--brand)] text-[var(--brand-foreground)] font-medium"
                    : inMonth ? "text-[var(--text-secondary)]" : "text-[var(--text-quaternary)]"
                }`}
              >
                {d.getDate()}
              </div>
              <div className="space-y-[2px]">
                {items.slice(0, MAX).map(({ agent, task }) => (
                  <TaskChip
                    key={`${agent.id}-${task.id}`}
                    agent={agent}
                    task={task}
                    compact
                    onDragStart={() => { dragRef.current = { agentId: agent.id, taskId: task.id, sourceDow: d.getDay() }; }}
                    onDragEnd={() => { dragRef.current = null; setHoverDi(null); }}
                    onClick={() => onClickTask(agent, task)}
                  />
                ))}
                {items.length > MAX && (
                  <button
                    onClick={(e) => { e.stopPropagation(); onOverflowDay(d); }}
                    className="press w-full text-left px-1.5 text-[10.5px] font-medium text-[var(--text-tertiary)] hover:text-[var(--text-primary)]"
                  >
                    +{items.length - MAX} more
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* -------------------------------- year view -------------------------------- */

function YearView({ cursor, agents, hidden, onPickDay, onPickMonth }: {
  cursor: Date;
  agents: Agent[];
  hidden: Set<string>;
  onPickDay: (d: Date) => void;
  onPickMonth: (d: Date) => void;
}) {
  const now = new Date();
  return (
    <div className="flex-1 min-h-0 overflow-y-auto fade-in">
      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-x-8 gap-y-7 px-8 py-6 max-w-[1180px] mx-auto">
        {MONTHS.map((name, mi) => {
          const first = new Date(cursor.getFullYear(), mi, 1);
          const start = startOfWeek(first);
          const cells = Array.from({ length: 42 }, (_, i) => addDays(start, i));
          return (
            <div key={mi}>
              <button
                onClick={() => onPickMonth(first)}
                className="press text-[13px] font-semibold tracking-[-0.005em] text-[var(--text-primary)] hover:underline mb-1.5"
              >
                {name}
              </button>
              <div className="grid grid-cols-7">
                {DOW.map((d) => (
                  <div key={d} className="h-5 flex items-center justify-center text-[9.5px] font-medium text-[var(--text-quaternary)]">
                    {d[0]}
                  </div>
                ))}
                {cells.map((d, i) => {
                  const inMonth = d.getMonth() === mi;
                  const items = inMonth ? tasksOn(agents, d, hidden) : [];
                  const today = sameDay(d, now);
                  return (
                    <button
                      key={i}
                      onClick={() => inMonth && onPickDay(d)}
                      className={`press h-7 flex flex-col items-center justify-center rounded-[6px] ${
                        inMonth ? "hover:bg-[var(--accent-hover)]" : "pointer-events-none"
                      }`}
                    >
                      <span
                        className={`w-[18px] h-[18px] flex items-center justify-center rounded-full text-[10.5px] tabular-nums ${
                          today
                            ? "bg-[var(--brand)] text-[var(--brand-foreground)] font-medium"
                            : inMonth ? "text-[var(--text-secondary)]" : "text-transparent"
                        }`}
                      >
                        {d.getDate()}
                      </span>
                      <span className="h-[4px] flex items-center gap-[2px]">
                        {items.slice(0, 3).map(({ agent }, j) => (
                          <span
                            key={j}
                            className="w-[3px] h-[3px] rounded-full"
                            style={{ background: `hsl(${agent.hue} 55% 50%)` }}
                          />
                        ))}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ------------------------------- agenda view ------------------------------- */

const PausedChip = () => (
  <span className="shrink-0 text-[10.5px] font-medium px-1.5 py-[2px] rounded-full text-[var(--warn-text)] bg-[var(--warn-bg)] ring-1 ring-[var(--warn-ring)]">
    Paused
  </span>
);

// Chronological list of the next 14 days of occurrences, plus an
// "Unscheduled" section for custom-cron / spec-less tasks that the
// calendar grids can't place.
function AgendaView({ cursor, agents, hidden, onClickTask }: {
  cursor: Date;
  agents: Agent[];
  hidden: Set<string>;
  onClickTask: (agent: Agent, task: AgentTask) => void;
}) {
  const now = new Date();
  const start = startOfDay(cursor);
  const days = Array.from({ length: 14 }, (_, i) => addDays(start, i));
  const groups = days
    .map((d) => ({ d, items: tasksOn(agents, d, hidden) }))
    .filter((g) => g.items.length > 0);

  const unscheduled: { agent: Agent; task: AgentTask }[] = [];
  for (const a of agents) {
    if (hidden.has(a.id)) continue;
    for (const t of a.tasks || [])
      if (!t.spec || (t.spec.mode === "recurring" && t.spec.freq === "custom"))
        unscheduled.push({ agent: a, task: t });
  }

  const dayLabel = (d: Date) => {
    const base = d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
    if (sameDay(d, now)) return `Today · ${base}`;
    if (sameDay(d, addDays(now, 1))) return `Tomorrow · ${base}`;
    return base;
  };

  return (
    <div className="flex-1 min-h-0 overflow-y-auto fade-in">
      <div className="max-w-[720px] mx-auto px-4 md:px-6 py-6 space-y-6">
        {groups.length === 0 && unscheduled.length === 0 && (
          <div className="py-14 text-center tray-box">
            <CalendarDays className="w-5 h-5 mx-auto mb-2 text-[var(--text-quaternary)]" strokeWidth={1.5} />
            <div className="text-[14px] font-medium text-[var(--text-primary)]">Nothing scheduled</div>
            <div className="text-[12.5px] text-[var(--text-tertiary)] mt-1">
              No occurrences in the next 14 days.
            </div>
          </div>
        )}

        {groups.map(({ d, items }) => (
          <section key={+d}>
            <div className="section-label mb-2">{dayLabel(d)}</div>
            <div className="rounded-[12px] ring-1 ring-[var(--hairline)] overflow-hidden divide-y divide-[var(--hairline)] bg-[var(--surface)]">
              {items.map(({ agent, task }) => (
                <button
                  key={`${agent.id}-${task.id}`}
                  onClick={() => onClickTask(agent, task)}
                  className="press w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-[var(--accent-hover)]"
                >
                  <span className={`w-[60px] shrink-0 text-[12.5px] tabular-nums text-[var(--text-tertiary)] ${task.paused ? "opacity-55" : ""}`}>
                    {fmtTime(task.spec.time)}
                  </span>
                  <span className={task.paused ? "opacity-55" : ""}>
                    <PixelAvatar seed={agent.seed} hue={agent.hue} emotion={agentMood(agent)} size={18} ring rounded={5} />
                  </span>
                  <span className={`min-w-0 flex-1 ${task.paused ? "opacity-55" : ""}`}>
                    <span className="flex items-center gap-2 min-w-0">
                      <span className="text-[13px] font-medium text-[var(--text-primary)] truncate">
                        {task.name}
                      </span>
                      {task.paused && <PausedChip />}
                    </span>
                    {/* On a phone the schedule sits under the name, which keeps the row's width */}
                    <span className="sm:hidden flex mt-1"><ScheduleBadge>{task.schedule}</ScheduleBadge></span>
                  </span>
                  <span className="hidden sm:flex"><ScheduleBadge>{task.schedule}</ScheduleBadge></span>
                </button>
              ))}
            </div>
          </section>
        ))}

        {unscheduled.length > 0 && (
          <section>
            <div className="section-label mb-1">Unscheduled</div>
            <p className="text-[12.5px] text-[var(--text-tertiary)] m-0 mb-2">
              These tasks use a custom cron and don’t appear on the calendar. Click one to edit its schedule.
            </p>
            <div className="rounded-[12px] ring-1 ring-[var(--hairline)] overflow-hidden divide-y divide-[var(--hairline)] bg-[var(--surface)]">
              {unscheduled.map(({ agent, task }) => (
                <button
                  key={`${agent.id}-${task.id}`}
                  onClick={() => onClickTask(agent, task)}
                  className="press w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-[var(--accent-hover)]"
                >
                  <span className={task.paused ? "opacity-55" : ""}>
                    <PixelAvatar seed={agent.seed} hue={agent.hue} emotion={agentMood(agent)} size={18} ring rounded={5} />
                  </span>
                  <span className={`min-w-0 flex-1 ${task.paused ? "opacity-55" : ""}`}>
                    <span className="flex items-center gap-2 min-w-0">
                      <span className="text-[13px] font-medium text-[var(--text-primary)] truncate">
                        {task.name}
                      </span>
                      {task.paused && <PausedChip />}
                    </span>
                    {/* On a phone the cron goes under the name, which keeps the row's width */}
                    <span className="sm:hidden block mt-0.5 font-mono text-[11px] text-[var(--text-quaternary)] truncate">
                      {task.cron || "no schedule"}
                    </span>
                  </span>
                  <span className="hidden sm:block shrink-0 font-mono text-[11px] text-[var(--text-quaternary)]">
                    {task.cron || "no schedule"}
                  </span>
                </button>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}

/* ---------------------------------- page ----------------------------------- */

type CalendarView = "week" | "month" | "year" | "agenda";

export interface SchedulePageProps {
  agents: Agent[];
  /* Resolve when the write landed, reject when the API refused (TasksPage's props). */
  onAddTask: (agentId: string, task: AgentTask) => void | Promise<void>;
  onUpdateTask: (agentId: string, taskId: string, patch: Partial<AgentTask>) => void | Promise<void>;
  onDeleteTask: (agentId: string, taskId: string) => void | Promise<void>;
  /** App wires this up but the page never runs a task, so a run started from
      Schedule logs no trace — unlike one started from Tasks. Declared because
      it is passed; see the smell register. */
  onRunTask?: (agentId: string, task: AgentTask) => void;
}

export default function SchedulePage({ agents, onAddTask, onUpdateTask, onDeleteTask }: SchedulePageProps) {
  // Seven day-columns don't survive a phone — start on the agenda there. Week
  // is still one tap away in the switcher.
  const { isDesktop } = useViewport();
  const [view, setView] = useState<CalendarView>(isDesktop ? "week" : "agenda");
  // Crossing the breakpoint (a rotated tablet, a narrowed window) lands on that size's view
  // instead of squeezing seven columns into a phone.
  useEffect(() => setView(isDesktop ? "week" : "agenda"), [isDesktop]);
  const [cursor, setCursor] = useState(() => new Date());
  const [hidden, setHidden] = useState<Set<string>>(() => new Set());
  const [draft, setDraft] = useState<TaskDraft | null>(null); // TaskDialog payload
  const dragRef = useRef<DragPayload | null>(null);

  const days = useMemo(
    () => Array.from({ length: 7 }, (_, i) => addDays(startOfWeek(cursor), i)),
    [cursor]
  );

  const totalTasks = agents.reduce((n, a) => n + (a.tasks || []).length, 0);

  const step = (dir: number) =>
    setCursor((c) =>
      view === "week"
        ? addDays(c, dir * 7)
        : view === "agenda"
          ? addDays(c, dir * 14)
          : view === "month"
            ? addMonths(c, dir)
            : addMonths(c, dir * 12)
    );

  const rangeLabel = (a: Date, b: Date) => {
    const am = a.toLocaleDateString(undefined, { month: "short" });
    const bm = b.toLocaleDateString(undefined, { month: "short" });
    return am === bm
      ? `${am} ${a.getDate()} – ${b.getDate()}, ${b.getFullYear()}`
      : `${am} ${a.getDate()} – ${bm} ${b.getDate()}, ${b.getFullYear()}`;
  };

  const label =
    view === "week"
      ? rangeLabel(days[0], days[6])
      : view === "agenda"
        ? rangeLabel(startOfDay(cursor), addDays(startOfDay(cursor), 13))
        : view === "month"
          ? cursor.toLocaleDateString(undefined, { month: "long", year: "numeric" })
          : String(cursor.getFullYear());

  const toggleAgent = (id: string) =>
    setHidden((h) => {
      const n = new Set(h);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });

  // Drop handler shared by week (newTime set) and month (newTime null → keep time).
  const handleMove = (targetDate: Date, newTime: string | null) => {
    const drag = dragRef.current;
    if (!drag) return;
    const agent = agents.find((a) => a.id === drag.agentId);
    const task = agent?.tasks!.find((t) => t.id === drag.taskId);
    if (!task) return;
    // A drag can only start from a chip, so past this guard the agent exists
    // and the task carries a spec.
    const spec = movedSpec(task.spec!, drag.sourceDow, targetDate, newTime ?? task.spec!.time);
    const moved = onUpdateTask(agent!.id, task.id, {
      spec,
      schedule: chipLabel(spec),
      cron: spec.mode === "recurring" ? (spec.freq === "custom" ? spec.cron : buildCron(spec)) : null,
    });
    dragRef.current = null;
    // Recurring tasks: moving one occurrence rewrites the rule, so be explicit.
    whenSaved(moved, () =>
      spec.mode === "once"
        ? toast(`“${task.name}” rescheduled`, { description: chipLabel(spec) })
        : toast(`“${task.name}” moved — applies to all future occurrences`, { description: chipLabel(spec) }),
    );
  };

  const openCreate = (date: Date | null, time?: string) =>
    setDraft({
      key: Date.now(),
      agentId: agents[0]?.id,
      task: null,
      spec: date
        ? { ...initialSchedule, mode: "once", date: isoDate(date), time: time || "09:00" }
        : initialSchedule,
    });

  const openEdit = (agent: Agent, task: AgentTask) =>
    setDraft({ key: task.id, agentId: agent.id, task });

  const saveDraft = async (agentId: string, task: AgentTask, editing: boolean) => {
    if (editing) {
      const { id, ...fields } = task;
      await onUpdateTask(agentId, id, fields);
      toast(`“${task.name}” updated`, { description: task.schedule });
    } else {
      await onAddTask(agentId, task);
      toast(`“${task.name}” scheduled`, { description: task.schedule });
    }
  };

  return (
    <main className="flex-1 min-w-0 flex flex-col bg-[var(--canvas)]">
      <PageHeader
        title={
          <>
            <Breadcrumb>Schedule</Breadcrumb>
            <button
              onClick={() => setCursor(new Date())}
              className="press h-7 px-2.5 rounded-[7px] text-[12.5px] font-medium text-[var(--text-secondary)] ring-1 ring-[var(--hairline-strong)] hover:text-[var(--text-primary)] hover:ring-[var(--hairline-focus)]"
            >
              Today
            </button>
            <div className="flex items-center">
              <IconButton size="sm" onClick={() => step(-1)} title="Previous">
                <ChevronLeft className="w-4 h-4" strokeWidth={1.75} />
              </IconButton>
              <IconButton size="sm" onClick={() => step(1)} title="Next">
                <ChevronRight className="w-4 h-4" strokeWidth={1.75} />
              </IconButton>
            </div>
            <div className="text-[14px] font-medium tracking-[-0.005em] text-[var(--text-primary)] tabular-nums whitespace-nowrap">
              {label}
            </div>
          </>
        }
      >

        {/* Agent filter — inline avatars on md+ */}
        <div className="hidden md:flex items-center gap-1 mr-1">
          {agents.map((a) => {
            const off = hidden.has(a.id);
            return (
              <button
                key={a.id}
                onClick={() => toggleAgent(a.id)}
                title={`${off ? "Show" : "Hide"} ${a.name}`}
                className={`press w-7 h-7 rounded-[7px] flex items-center justify-center ${
                  off ? "opacity-30 grayscale" : "hover:bg-[var(--accent-hover)]"
                }`}
              >
                <PixelAvatar seed={a.seed} hue={a.hue} emotion={agentMood(a)} size={18} ring rounded={5} />
              </button>
            );
          })}
        </div>

        {/* Agent filter — compact dropdown below md */}
        <Popover>
          <PopoverTrigger asChild>
            <button
              title="Filter agents"
              className="press md:hidden relative shrink-0 w-7 h-7 rounded-[7px] flex items-center justify-center text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--accent-hover)]"
            >
              <Users className="w-4 h-4" strokeWidth={1.75} />
              {hidden.size > 0 && (
                <span className="absolute top-[3px] right-[3px] w-1.5 h-1.5 rounded-full bg-[var(--brand)]" />
              )}
            </button>
          </PopoverTrigger>
          <PopoverContent
            align="end"
            sideOffset={6}
            className="w-[190px] p-1 rounded-[12px] bg-[var(--surface)] border border-[var(--hairline-strong)] shadow-menu"
          >
            {agents.map((a) => {
              const off = hidden.has(a.id);
              return (
                <button
                  key={a.id}
                  onClick={() => toggleAgent(a.id)}
                  className="press w-full flex items-center gap-2 px-2 h-8 rounded-[7px] text-[13px] font-medium text-[var(--text-secondary)] hover:bg-[var(--accent-hover)] hover:text-[var(--text-primary)]"
                >
                  <span className={off ? "opacity-30 grayscale" : ""}>
                    <PixelAvatar seed={a.seed} hue={a.hue} emotion={agentMood(a)} size={16} ring rounded={5} />
                  </span>
                  <span className={`flex-1 text-left truncate ${off ? "text-[var(--text-quaternary)]" : ""}`}>
                    {a.name}
                  </span>
                  {!off && <Check className="w-3.5 h-3.5 text-[var(--text-tertiary)]" strokeWidth={2} />}
                </button>
              );
            })}
          </PopoverContent>
        </Popover>

        {/* View switcher */}
        <div className="flex gap-0.5 p-0.5 rounded-[8px] bg-[var(--surface-sunk)] ring-1 ring-[var(--hairline)]">
          {(["week", "month", "year", "agenda"] as const).map((v) => (
            <button
              key={v}
              onClick={() => setView(v)}
              className={`press h-6.5 px-2.5 py-1 rounded-[6px] text-[12.5px] font-medium capitalize ${
                view === v
                  ? "bg-[var(--surface)] text-[var(--text-primary)] ring-1 ring-[var(--hairline)] shadow-lift"
                  : "text-[var(--text-tertiary)] hover:text-[var(--text-primary)]"
              }`}
            >
              {v}
            </button>
          ))}
        </div>

        <BrandButton onClick={() => openCreate(null)} className="shrink-0">
          <Plus className="w-3.5 h-3.5" strokeWidth={2} />
          New task
        </BrandButton>
      </PageHeader>

      {totalTasks === 0 && (
        <div className="shrink-0 px-4 py-2 border-b hairline bg-[var(--surface-sunk)] text-[12.5px] text-[var(--text-tertiary)] fade-in">
          No scheduled tasks yet — click any time slot on the calendar, or hit{" "}
          <span className="font-medium text-[var(--text-secondary)]">New task</span>.
        </div>
      )}

      {view === "week" ? (
        <WeekView
          days={days}
          agents={agents}
          hidden={hidden}
          dragRef={dragRef}
          onMove={handleMove}
          onClickTask={openEdit}
          onClickSlot={(d, t) => openCreate(d, t)}
        />
      ) : view === "month" ? (
        <MonthView
          cursor={cursor}
          agents={agents}
          hidden={hidden}
          dragRef={dragRef}
          onMove={handleMove}
          onClickTask={openEdit}
          onClickDay={(d) => openCreate(d, "09:00")}
          onOverflowDay={(d) => { setCursor(d); setView("week"); }}
        />
      ) : view === "year" ? (
        <YearView
          cursor={cursor}
          agents={agents}
          hidden={hidden}
          onPickDay={(d) => { setCursor(d); setView("week"); }}
          onPickMonth={(d) => { setCursor(d); setView("month"); }}
        />
      ) : (
        <AgendaView
          cursor={cursor}
          agents={agents}
          hidden={hidden}
          onClickTask={openEdit}
        />
      )}

      {draft && (
        <TaskDialog
          key={draft.key}
          open={!!draft}
          onOpenChange={(v) => !v && setDraft(null)}
          agents={agents}
          draft={draft}
          onSave={saveDraft}
          onDelete={(agentId, taskId) => {
            const t = agents.find((a) => a.id === agentId)?.tasks!.find((x) => x.id === taskId);
            whenSaved(onDeleteTask(agentId, taskId), () => toast(`“${t?.name || "Task"}” deleted`));
          }}
        />
      )}
    </main>
  );
}
